import noop from 'lodash/noop';
import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  use,
  useState,
} from 'react';
import {
  FitClientBasicsFragment,
  UserBasicsFragment,
} from '../../graphql/types';

export enum ChatSize {
  Closed = 'CLOSED',
  Minimized = 'MINIMIZED',
  Open = 'OPEN',
  Maximized = 'MAXIMIZED',
}

export type MemberChatBasicInfo = Pick<
  UserBasicsFragment,
  'id' | 'firstName' | 'lastName' | 'avatar' | 'lastActivity'
> & {
  fitClient?: FitClientBasicsFragment | null;
} & {
  fitCoach?: Pick<UserBasicsFragment, 'id' | 'firstName' | 'lastName'> | null;
};

type ChatContextType = {
  chatId: string | null | undefined;
  initialChatSize?: ChatSize;
  chatSize: ChatSize;
  setChatSize: Dispatch<SetStateAction<ChatSize>>;
  member: MemberChatBasicInfo | null | undefined;
  openChat: (options?: {
    member?: MemberChatBasicInfo;
    chatId?: string | null;
    updateChatSize?: boolean;
  }) => void;
  closeChat: () => void;
  chatListOpen: boolean;
  setChatListOpen: Dispatch<SetStateAction<boolean>>;
  filterByUnread: boolean;
  setFilterByUnread: Dispatch<SetStateAction<boolean>>;
};

export const defaultChatContextValues = {
  chatId: null,
  initialChatSize: ChatSize.Minimized,
  chatSize: ChatSize.Minimized,
  setChatSize: noop,
  member: null,
  openChat: noop,
  closeChat: noop,
  chatListOpen: false,
  setChatListOpen: noop,
  filterByUnread: false,
  setFilterByUnread: noop,
};

const ChatContext = createContext<ChatContextType>(defaultChatContextValues);

export const getChatContextForTesting = () => {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('getChatContextForTesting should only be used in tests');
  }
  return ChatContext;
};

interface ChatProviderProps extends PropsWithChildren {
  chatId?: string;
  initialChatSize?: ChatSize;
  member?: MemberChatBasicInfo | undefined | null;
  setMember?: (member: MemberChatBasicInfo | null) => void;
}
const ChatProvider: React.FC<ChatProviderProps> = ({
  children,
  chatId: initialChatId,
  initialChatSize = ChatSize.Minimized,
  member: initialMember = null,
}) => {
  const [member, setMember] = useState<ChatContextType['member']>();
  const [chatSize, setChatSize] = useState<ChatSize>(initialChatSize);
  const [chatId, setChatId] = useState<ChatContextType['chatId'] | undefined>();
  const [chatListOpen, setChatListOpen] = useState(false);
  const [filterByUnread, setFilterByUnread] = useState(false);
  const openChat: ChatContextType['openChat'] = (options) => {
    const {
      member: incomingMember,
      chatId: incomingChatId,
      updateChatSize = true,
    } = options || {};

    setMember(incomingMember);
    setChatId(incomingChatId);
    setChatListOpen(false);
    if (updateChatSize) {
      setChatSize(ChatSize.Open);
    }
  };

  const closeChat = () => {
    setMember(null);
    setChatId(null);
    setChatSize(ChatSize.Closed);
  };

  return (
    <ChatContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        chatId: initialChatId || chatId,
        chatSize,
        setChatSize,
        member: initialMember || member,
        openChat,
        closeChat,
        chatListOpen,
        setChatListOpen,
        filterByUnread,
        setFilterByUnread,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

function useChatContext(): ChatContextType {
  return use(ChatContext);
}

export { ChatProvider, useChatContext };
